import { useWorkspaceUploadQueue } from "@/pages/workspaces/hooks/useWorkspaceUploadQueue";
import {
  buildFolderUploadTreeFromFileList,
  traverseDirectoryHandleTree,
  type FileSystemDirectoryHandleLike,
} from "@/utils/traverseDataTransferItems";
import { useCallback, useRef } from "react";

/**
 * The two hidden pickers behind "Upload files" / "Upload folder", shared by
 * every surface that offers them (rail rows, page folder menus). Render
 * `uploadInputs` once near the surface's root; `uploadFilesTo(path)` /
 * `uploadFolderTo(path)` open the pickers targeting that folder. Folder
 * uploads use the native directory picker where available (Chromium) and the
 * webkitdirectory input elsewhere; both converge on `enqueueTree`, the same
 * entry point the OS drop path uses.
 */
export function useWorkspaceUploadPickers(workspaceId: string | undefined) {
  const uploadQueue = useWorkspaceUploadQueue(workspaceId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTargetPathRef = useRef("");

  const uploadFilesTo = useCallback((parentPath: string) => {
    uploadTargetPathRef.current = parentPath;
    fileInputRef.current?.click();
  }, []);

  const uploadFolderTo = useCallback(
    (parentPath: string) => {
      uploadTargetPathRef.current = parentPath;
      const picker = (
        window as unknown as {
          showDirectoryPicker?: () => Promise<FileSystemDirectoryHandleLike>;
        }
      ).showDirectoryPicker;
      if (!picker) {
        folderInputRef.current?.click();
        return;
      }
      void (async () => {
        try {
          const dir = await picker.call(window);
          uploadQueue.enqueueTree(
            await traverseDirectoryHandleTree(dir),
            parentPath,
          );
        } catch {
          // AbortError — the user cancelled the dialog.
        }
      })();
    },
    [uploadQueue],
  );

  const handleFilesSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      const parentPath = uploadTargetPathRef.current;
      if (files.length > 0 && workspaceId) {
        uploadQueue.enqueueFiles(files, parentPath);
      }
      e.target.value = "";
      uploadTargetPathRef.current = "";
    },
    [uploadQueue, workspaceId],
  );

  const handleFolderSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // The fallback folder picker delivers a flat FileList with
      // webkitRelativePath set (root folder name included).
      const files = Array.from(e.target.files ?? []);
      const parentPath = uploadTargetPathRef.current;
      if (files.length > 0 && workspaceId) {
        uploadQueue.enqueueTree(
          buildFolderUploadTreeFromFileList(files),
          parentPath,
        );
      }
      e.target.value = "";
      uploadTargetPathRef.current = "";
    },
    [uploadQueue, workspaceId],
  );

  const uploadInputs = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onClick={(e) => e.stopPropagation()}
        onChange={handleFilesSelected}
      />
      <input
        // React drops unknown non-string DOM attributes, so set the
        // non-standard directory-picker attributes imperatively.
        ref={(el) => {
          folderInputRef.current = el;
          if (el) {
            el.setAttribute("webkitdirectory", "");
            el.setAttribute("directory", "");
          }
        }}
        type="file"
        multiple
        className="hidden"
        // A programmatic .click() would otherwise bubble to the invoking row.
        onClick={(e) => e.stopPropagation()}
        onChange={handleFolderSelected}
      />
    </>
  );

  return { uploadQueue, uploadInputs, uploadFilesTo, uploadFolderTo };
}
